import React, { useEffect, useState } from 'react';

export interface CircularProgressProps {
  percentage: number;
  completedTasks: number;
  totalTasks: number;
  size?: number;
  strokeWidth?: number;
  animate?: boolean;
  className?: string;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  percentage,
  completedTasks,
  totalTasks,
  size = 200,
  strokeWidth = 16,
  animate = true,
  className = ''
}) => {
  const [animatedPercentage, setAnimatedPercentage] = useState(animate ? 0 : percentage);
  
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  
  const offset = circumference - (animatedPercentage / 100) * circumference;
  
  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => {
        setAnimatedPercentage(percentage);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setAnimatedPercentage(percentage);
    }
  }, [percentage, animate]);
  
  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size, filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))' }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <g transform={`rotate(-90 ${center} ${center})`}>
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-300 dark:text-gray-600/30"
          />

          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="text-brand-orange transition-[stroke-dashoffset] duration-[1500ms] ease-out motion-reduce:transition-none"
            style={{
              willChange: animate ? 'stroke-dashoffset' : 'auto'
            }}
          />
        </g>
      </svg>
      
      {/* Center text overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-5xl font-bold text-foreground leading-none" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {Math.round(percentage)}%
        </div>
      </div>
    </div>
  );
};
